import { apiUrl, buildQuery, requestJson } from './client'

export type VisualAsset = {
  id: string
  asset_type: 'image' | 'video'
  title: string
  file_url: string
  file_hash: string
  thumbnail_url: string | null
  derivatives: VisualAssetDerivative[]
  duration_seconds: number | null
  mime_type: string | null
  size_bytes: number | null
  status: string
  width: number | null
  height: number | null
  created_at: string
  groups: VisualGroupSummary[]
}

export type VisualAssetDerivative = {
  variant: 'thumb' | 'preview' | 'player'
  url: string
  mime_type: string
  width: number | null
  height: number | null
  size_bytes: number | null
}

export type ImageDerivative = {
  variant: 'thumb' | 'preview'
  url: string
  mime_type: string
  width: number | null
  height: number | null
  size_bytes: number | null
}

export type VisualGroupSummary = {
  id: string
  name: string
}

export type VisualGroup = {
  id: string
  name: string
  description: string | null
  asset_count: number
  channel_count: number
  created_at: string
  updated_at: string
}

export type VisualAssetFilters = {
  group?: string | undefined
  type?: 'image' | 'video' | undefined
  search?: string | undefined
  status?: string | undefined
}

export type VisualUploadSkippedFile = {
  filename: string
  duplicateOfId?: string
  reason: string
}

export type VisualUploadResult = {
  items: VisualAsset[]
  skipped: VisualUploadSkippedFile[]
}

export const getVisualDerivativeUrl = (
  asset: Pick<VisualAsset, 'asset_type' | 'derivatives' | 'file_url' | 'thumbnail_url'>,
  variant: VisualAssetDerivative['variant']
): string => {
  const derivative = asset.derivatives.find((item) => item.variant === variant)

  if (derivative) {
    return derivative.url
  }

  if (variant === 'thumb' && asset.thumbnail_url) {
    return asset.thumbnail_url
  }

  if (variant === 'player') {
    const previewDerivative = asset.derivatives.find((item) => item.variant === 'preview')

    if (previewDerivative) {
      return previewDerivative.url
    }
  }

  return asset.file_url
}

export const getVisualAssets = async (
  filters: VisualAssetFilters = {}
): Promise<VisualAsset[]> => {
  const payload = await requestJson<{ items: VisualAsset[] }>(
    `/api/admin/visuals${buildQuery(filters)}`
  )

  return payload.items
}

export const getVisualGroups = async (search?: string): Promise<VisualGroup[]> => {
  const payload = await requestJson<{ items: VisualGroup[] }>(
    `/api/admin/visual-groups${buildQuery({ search })}`
  )

  return payload.items
}

export const uploadVisualFile = async (
  file: File,
  visualGroupIds: string[] = [],
  onProgress?: (progress: number) => void
): Promise<VisualUploadResult> => {
  const formData = new FormData()

  visualGroupIds.forEach((id) => formData.append('visualGroupIds', id))
  formData.append('file', file)

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('POST', `${apiUrl}/api/admin/visuals/upload`)
    request.withCredentials = true

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return
      }

      onProgress?.(Math.round((event.loaded / event.total) * 100))
    }

    request.onload = () => {
      const payload = JSON.parse(request.responseText || '{}') as Partial<VisualUploadResult> & {
        message?: string
      }

      if (request.status < 200 || request.status >= 300) {
        reject(new Error(payload.message ?? `Request failed with status ${request.status}`))
        return
      }

      resolve({
        items: payload.items ?? [],
        skipped: payload.skipped ?? []
      })
    }

    request.onerror = () => reject(new Error('Upload failed'))
    request.onabort = () => reject(new Error('Upload cancelled'))

    request.send(formData)
  })
}

export const updateVisualAsset = async (input: {
  id: string
  title: string
}): Promise<VisualAsset> => {
  const payload = await requestJson<{ item: VisualAsset }>(
    `/api/admin/visuals/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: input.title
      })
    }
  )

  return payload.item
}

export const updateVisualAssetGroups = async (
  visualAssetId: string,
  visualGroupIds: string[]
): Promise<VisualAsset> => {
  const payload = await requestJson<{ item: VisualAsset }>(
    `/api/admin/visuals/${encodeURIComponent(visualAssetId)}/groups`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ visualGroupIds })
    }
  )

  return payload.item
}

export const deleteVisualAsset = async (visualAssetId: string): Promise<void> => {
  await requestJson(`/api/admin/visuals/${encodeURIComponent(visualAssetId)}`, {
    method: 'DELETE'
  })
}

export const createVisualGroup = async (input: {
  name: string
  description: string | null
}): Promise<VisualGroup> => {
  const payload = await requestJson<{ item: VisualGroup }>('/api/admin/visual-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateVisualGroup = async (input: {
  id: string
  name: string
  description: string | null
}): Promise<VisualGroup> => {
  const payload = await requestJson<{ item: VisualGroup }>(
    `/api/admin/visual-groups/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        description: input.description
      })
    }
  )

  return payload.item
}

export const deleteVisualGroup = async (visualGroupId: string): Promise<void> => {
  await requestJson(`/api/admin/visual-groups/${encodeURIComponent(visualGroupId)}`, {
    method: 'DELETE'
  })
}
